import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import PhoneNumberInput from "components/ui/phone-input";
import { useAppDispatch } from "../../../redux/useDispatch";
import { usePatchMeMutation } from "api/account/accountApiSlice";
import { AccountProfile } from "api/account/types";
import { setAccountProfile } from "../../../redux/slice/account/accountSlice";
import { isPlausiblePhone, isValidPhone } from "util/validation";

interface ProfileEditSectionProps {
  profile: AccountProfile;
}

/**
 * #1873 scenario 4 — the profile phone prefills the booking personal-data
 * step, so it must pass the EXACT same rule the booking step enforces
 * (`validatePersonalData` in bookingCreationSlice): structurally valid
 * (`isValidPhone`) AND not an obvious dummy pattern (`isPlausiblePhone`),
 * both from util/validation. Empty stays allowed — the profile phone is
 * optional. Neither rule throws: `isValidPhone` returns false on input the
 * parser rejects (e.g. too long), including values saved before this
 * validation existed.
 */
const isAcceptableProfilePhone = (value: string): boolean => {
  if (!value) return true;
  return isValidPhone(value) && isPlausiblePhone(value);
};

/**
 * The saved state the form is compared against, to decide whether there is
 * anything to save. Whitespace is normalized out of the phone because the
 * intl input re-emits the number with a space after the dial code — retyping
 * the same number must not count as a change.
 */
interface SavedProfileValues {
  firstName: string;
  lastName: string;
  phone: string;
}

const toSavedValues = (profile: AccountProfile): SavedProfileValues => ({
  firstName: (profile.firstName ?? "").trim(),
  lastName: (profile.lastName ?? "").trim(),
  phone: (profile.phone ?? "").replace(/\s+/g, ""),
});

/**
 * H-3a F2 — editable profile fields (first name, last name, phone). Saves via
 * PATCH /me and pushes the returned profile into the account slice. The email
 * is NOT edited here — it changes only through the OTP-verified email-change
 * flow (EmailChangeSection), and newsletter consent is no longer offered at
 * all (see the note further down).
 */
const ProfileEditSection = ({ profile }: ProfileEditSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [patchMe, { isLoading: isSaving }] = usePatchMeMutation();

  const [firstName, setFirstName] = useState(profile.firstName ?? "");
  const [lastName, setLastName] = useState(profile.lastName ?? "");
  const [phone, setPhone] = useState(profile.phone ?? "");
  // Start "touched" when the stored value is already invalid (saved before
  // this validation existed), so the error is visible immediately.
  const [phoneTouched, setPhoneTouched] = useState(
    () => !isAcceptableProfilePhone(profile.phone ?? "")
  );
  // Start "touched" when the stored value is already missing, so an account
  // that has never filled its name sees why it cannot save — rather than a
  // dead button with no explanation.
  const [firstNameTouched, setFirstNameTouched] = useState(
    () => (profile.firstName ?? "").trim() === ""
  );
  const [lastNameTouched, setLastNameTouched] = useState(
    () => (profile.lastName ?? "").trim() === ""
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // What is currently stored, so the form can tell whether there is anything
  // to save. Re-seeded from the PATCH RESPONSE rather than from the local
  // fields: the server is the authority on what was persisted, so if it trims
  // or reformats a value the baseline reflects that rather than our guess.
  const [savedValues, setSavedValues] = useState<SavedProfileValues>(() =>
    toSavedValues(profile)
  );

  const phoneValid = isAcceptableProfilePhone(phone);
  const showPhoneError = phoneTouched && !phoneValid;

  // Reported by the CCO on staging: "Änderungen speichern" stayed active after
  // a successful save, so the page gave no sign the save had taken — the
  // button looked exactly as it had before pressing it, and pressing it again
  // re-sent an identical PATCH. It was never dirty-aware at all: on a complete
  // profile it was enabled from the moment the page loaded.
  const isDirty =
    firstName.trim() !== savedValues.firstName ||
    lastName.trim() !== savedValues.lastName ||
    phone.replace(/\s+/g, "") !== savedValues.phone;

  // First and last name are REQUIRED (PO, 2026-08-20): every mail we send
  // addresses the person by name, and an e-mail address is frequently no clue
  // to what that name is. An OTP account starts with neither, so this is
  // enforced when the profile is SAVED, not as a wall in front of the app —
  // and the API refuses a blank name independently (`meController`), because a
  // rule that lives only in the form is not a rule (ADR-0020).
  const firstNameMissing = firstName.trim() === "";
  const lastNameMissing = lastName.trim() === "";
  const showFirstNameError = firstNameTouched && firstNameMissing;
  const showLastNameError = lastNameTouched && lastNameMissing;

  const handlePhoneChange = (value: string) => {
    const trimmed = value.trim();
    // Dial code alone (e.g. "+49") means the field is empty — mirrors the
    // booking flow's `phoneChanged` reducer.
    setPhone(/^\+\d{0,3}$/.test(trimmed) ? "" : trimmed);
  };

  const handleSave = async () => {
    if (firstNameMissing || lastNameMissing) {
      setFirstNameTouched(true);
      setLastNameTouched(true);
      return;
    }
    if (!phoneValid) {
      setPhoneTouched(true);
      return;
    }
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const updated = await patchMe({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        // `newsletterConsent` is deliberately NOT sent. The backend stamps
        // `newsletter_consent_at = now` whenever the field is present, so
        // echoing the stored value back would refresh the consent timestamp on
        // every profile save — re-dating a consent nobody gave again.
      }).unwrap();
      dispatch(setAccountProfile(updated));
      setSavedValues(toSavedValues(updated));
      setSaveSuccess(true);
    } catch {
      setSaveError(t("account.profile.save_error"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label
          htmlFor="account-first-name"
          className="text-sm font-medium text-slate-700"
        >
          {t("account.profile.first_name_label")}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <Input
          id="account-first-name"
          autoComplete="given-name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          onBlur={() => setFirstNameTouched(true)}
          disabled={isSaving}
          error={showFirstNameError}
          aria-invalid={showFirstNameError || undefined}
          className="w-full"
        />
        {showFirstNameError && (
          <p className="text-sm text-red-600">
            {t("account.profile.first_name_required")}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label
          htmlFor="account-last-name"
          className="text-sm font-medium text-slate-700"
        >
          {t("account.profile.last_name_label")}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <Input
          id="account-last-name"
          autoComplete="family-name"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          onBlur={() => setLastNameTouched(true)}
          disabled={isSaving}
          error={showLastNameError}
          aria-invalid={showLastNameError || undefined}
          className="w-full"
        />
        {showLastNameError && (
          <p className="text-sm text-red-600">
            {t("account.profile.last_name_required")}
          </p>
        )}
      </div>

      {/* The address itself belongs with the rest of the personal data; only
          the machinery for CHANGING it is elsewhere, behind its own button
          (EmailChangeSection). Read-only here — an e-mail change is an
          OTP-verified ceremony, not a text edit. */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-slate-700">
          {t("account.profile.email_label")}
        </p>
        <p className="text-sm text-foreground">{profile.email}</p>
      </div>

      <div className="space-y-2">
        <label
          htmlFor="account-phone"
          className="text-sm font-medium text-slate-700"
        >
          {t("account.profile.phone_label")}
        </label>
        <PhoneNumberInput
          id="account-phone"
          value={phone}
          onChange={handlePhoneChange}
          onBlur={() => setPhoneTouched(true)}
          autoComplete="tel"
          disabled={isSaving}
          error={showPhoneError}
          aria-invalid={showPhoneError || undefined}
          aria-describedby={showPhoneError ? "account-phone-error" : undefined}
        />
        {showPhoneError && (
          <p id="account-phone-error" className="text-sm text-red-600">
            {t("book_location.personal_data.validation.phone_invalid")}
          </p>
        )}
      </div>

      {/* The newsletter opt-in is NOT rendered (PO, 2026-08-20). Neither
          kolula nor every tenant runs a newsletter, and `newsletter_consent`
          is written to the account, returned in the GDPR export and cleared on
          deletion — but consumed by nothing: no list, no sync, no mail. So the
          checkbox recorded a consent, with a timestamp, for something that
          could never arrive.

          The field stays on the API and in the export so existing values are
          not lost or hidden from the person they belong to. It comes back when
          a tenant can declare that it runs a newsletter and say where the
          addresses go (#2102). Until then the value is simply never changed
          from here. */}

      {saveError && <p className="text-sm text-red-600">{saveError}</p>}
      {/* Derived, not a second effect writing the same state (CLAUDE.md: one
          source of truth): editing again makes the confirmation stale, and a
          "saved" message next to an active button is the same lie the button
          was telling. */}
      {saveSuccess && !isDirty && (
        <p className="text-sm text-slate-600" aria-live="polite">
          {t("account.profile.save_success")}
        </p>
      )}

      <Button
        size="lg"
        variant="primary"
        className="w-full"
        disabled={
          isSaving ||
          !isDirty ||
          !phoneValid ||
          firstNameMissing ||
          lastNameMissing
        }
        onClick={handleSave}
      >
        {isSaving ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t("account.profile.saving")}
          </>
        ) : (
          t("account.profile.save")
        )}
      </Button>
    </div>
  );
};

export default ProfileEditSection;
